import { Component, PLATFORM_ID, Renderer2, inject, computed, effect, HostListener, ChangeDetectorRef, DOCUMENT } from '@angular/core';
import { isPlatformBrowser, ViewportScroller, isPlatformServer } from '@angular/common';
import {
  Router,
  NavigationEnd,
  RouterOutlet,
  NavigationStart,
  NavigationCancel,
  NavigationError,
  Scroll,
  NavigationSkipped,
  RouterLink,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, getDictionaryLoaded } from '@teamfoster/sdk/dictionary-ngrx';

import * as navActions from './store/actions/nav.action';
import * as navSelectors from './store/selectors/nav.selector';
import { debounceTime, filter } from 'rxjs/operators';
import { ChangeDetectionStrategy } from '@angular/core';
import { CookieConfigService, CookieNoticeComponent, getCookiePreferences, ShowCookieSettings } from '@teamfoster/sdk/cookie-ngrx';
import { getRouterState, getThemeState, RouterStateUrl, SetTheme } from './store';
import { FromMenuPipe, getMenuLoaded } from '@teamfoster/sdk/menu-ngrx';
import { getTheme, getThemeLogo } from './store/selectors/theme.selector';
import { toSignal } from '@angular/core/rxjs-interop';
import { TopbarComponent } from './standalone/components/topbar/topbar.component';
import { FooterComponent } from './standalone/components/footer/footer.component';
import { LoaderIndicatorComponent } from './standalone/components/loading-indicator/loading-indicator.component';
import { GridOverlayComponent } from './standalone/components/grid-overlay/grid-overlay.component';
import { routerFade } from './animations';
import { styleguideConfig } from './styleguide/styleguide-config';
import { getActivePortal, getAllSwitcherPortals } from './portals/store';
import { SocialChannelButtonsComponent } from './social-media/components/social-channel-buttons/social-channel-buttons.component';
import { PartnerListComponent } from './standalone/components/partner-list/partner-list.component';
import { RouterReducerState } from '@ngrx/router-store';
import { getSelectedLandingPage } from './landing-pages/store';
import { IconComponent } from '@teamfoster/sdk/icon';
import { fromEvent } from 'rxjs';
import { Portal } from './portals/models';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routerFade],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TopbarComponent,
    FooterComponent,
    LoaderIndicatorComponent,
    CookieNoticeComponent,
    FromDictionaryPipe,
    GridOverlayComponent,
    FromMenuPipe,
    SocialChannelButtonsComponent,
    PartnerListComponent,
    IconComponent,
    RouterLink,
  ],
})
export class AppComponent {
  private store = inject(Store);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private viewportScroller = inject(ViewportScroller);
  private platformId = inject(PLATFORM_ID);
  private document: any = inject(DOCUMENT);
  private cookieConfig = inject(CookieConfigService);
  private cd = inject(ChangeDetectorRef);

  activePortal$ = this.store.selectSignal(getActivePortal);
  activeLandingPage$ = this.store.selectSignal(getSelectedLandingPage);

  routeState$ = this.store.selectSignal(getRouterState);
  menuOpen$ = this.store.selectSignal(navSelectors.getMenuOpen);
  menuTheme$ = this.store.selectSignal(navSelectors.getMenuTheme);
  showProgramCTAButton$ = this.store.selectSignal(navSelectors.getShowProgramCTAButton);
  cookiePrefs$ = this.store.selectSignal(getCookiePreferences);
  themeState$ = this.store.selectSignal(getThemeState);
  themeLogo$ = this.store.selectSignal(getThemeLogo);
  theme$ = this.store.selectSignal(getTheme);

  dictionaryLoaded$ = this.store.selectSignal(getDictionaryLoaded);
  menuLoaded$ = this.store.selectSignal(getMenuLoaded);
  switcherPortals$ = this.store.selectSignal(getAllSwitcherPortals);

  scrollUpdate = toSignal(fromEvent(this.document, 'scroll').pipe(debounceTime(5)));
  fixed = computed(() => {
    const e = this.scrollUpdate(); // trigger on scrollUpdate
    return (isPlatformServer(this.platformId) ? 0 : window.scrollY) > 104;
  });

  landingPage = this.store.selectSignal(getSelectedLandingPage);

  portalTheme = effect(() => {
    const portal = this.activePortal$();
    if (portal && !this.landingPage()?.segment?.theme) {
      console.log('[PORTAL] SetTheme', portal.bodyCssClass || 'dark');
      this.store.dispatch(
        SetTheme({ theme: portal.bodyCssClass || 'dark', logoUrl: portal.logoUrl, lightModeLogoUrl: portal.lightModeLogoUrl })
      );
    }
  });

  cookieUpdate = effect(() => {
    const cookiePrefs = this.cookiePrefs$();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (cookiePrefs?.marketing) {
      this.loadFacebookPixel(this.activePortal$()!);
      this.initRobinChat();
    }
  });

  private routerEvents$ = toSignal(
    this.router.events.pipe(
      filter(e => [NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Scroll].some(b => e instanceof b))
    )
  );
  loading$ = computed(
    () =>
      !(
        this.routerEvents$() instanceof NavigationEnd ||
        this.routerEvents$() instanceof NavigationCancel ||
        this.routerEvents$() instanceof NavigationError ||
        this.routerEvents$() instanceof Scroll
      )
  );

  loaded$ = computed(() => this.dictionaryLoaded$() && this.menuLoaded$());

  domainName = this.document.location?.hostname;
  testGridEnabled = false;

  testThemes = false;
  styleguideConfig = styleguideConfig;

  menuOpenEffect$ = effect(() => this.updateRoot(this.menuOpen$()));
  routerEffect$ = effect(() => {
    const event = this.routerEvents$();
    const state = this.routeState$();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    //if (event instanceof NavigationStart && event.navigationTrigger === 'imperative' && !state?.state?.queryParams) {
    //  window.scrollTo(0, 0);
    //}
    if (event instanceof NavigationEnd && this.cookiePrefs$()?.analytical) {
      if ((<any>window).gtag) {
        (<any>window).gtag('config', this.activePortal$()?.analyticsTrackingCode, {
          page_path: event.urlAfterRedirects,
          anonymize_ip: true,
        });
      }
      if ((<any>window).fbq) {
        (<any>window).fbq('track', 'PageView ');
      }
    }

    if (event instanceof Scroll && !(event.routerEvent instanceof NavigationSkipped || event.routerEvent instanceof NavigationCancel)) {
      this.handleScroll(event, state);
    }

    this.document.querySelector('body').classList.add('set--in');
    this.document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  });

  searchActive = computed(() => {
    const state = this.routeState$();
    return state?.state?.url.includes('/zoeken');
  });

  private previousPath = '';

  @HostListener('keydown.escape', ['$event'])
  esc() {
    this.closeMenu();
  }

  toggleMenu() {
    this.store.dispatch(navActions.ToggleMenu());
  }

  closeMenu() {
    this.store.dispatch(navActions.CloseMenu());
  }

  openMenu() {
    this.store.dispatch(navActions.OpenMenu());
  }

  openCookiePreferences() {
    this.store.dispatch(ShowCookieSettings());
  }

  private updateRoot(menuActive: boolean) {
    menuActive
      ? this.renderer.addClass(this.document.body, 'main-nav--active')
      : this.renderer.removeClass(this.document.body, 'main-nav--active');
  }

  handleScroll(event: Scroll, state: RouterReducerState<RouterStateUrl>) {
    const urlFragment = event.anchor;
    // if we can smooth scroll to the fragment, do it. Otherwise fallback on build in anchor scrolling
    if (event.routerEvent.id > 1 && urlFragment && document.getElementById(urlFragment)) {
      this.smoothScroll(document.getElementById(urlFragment));
      return;
    }
    if (event.anchor) {
      // If anchor exists, scroll to anchor
      this.viewportScroller.scrollToAnchor(event.anchor);
    } else if (event.position) {
      // Scroll to the saved position
      this.viewportScroller.scrollToPosition(event.position);
    } else {
      // Scroll to the top
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }

  private smoothScroll(targetElement: HTMLElement | null): void {
    if (!targetElement) {
      return;
    }
    const yOffset = -120; // Your offset value
    const yPosition = targetElement.getBoundingClientRect().top + window.scrollY + yOffset;

    setTimeout(() => {
      window.scrollTo({
        top: yPosition,
        behavior: 'smooth',
      });
    }, 0);
  }

  private loadFacebookPixel(portal: Portal) {
    if (portal.facebookPixelCode && typeof (<any>window)['fbq'] === 'undefined') {
      //console.log(portal.facebookPixelCode);
      const $script = document.createElement('script');
      $script.id = 'Facebook';
      $script.async = true;
      $script.innerHTML = `!function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            }; if (!f._fbq) f._fbq = n;
            n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
        }(window,
            document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${portal.facebookPixelCode}');
        fbq('track', 'ViewContent');`;

      document.head.prepend($script);
    }
  }

  getRouteAnimationState(outlet: RouterOutlet) {
    // Only animate if url differs, not queryparams or fragment etc.
    if (outlet.activatedRouteData && outlet.isActivated) {
      const path = outlet.activatedRoute.snapshot.pathFromRoot
        .map(v => v.url.map(segment => segment.toString()))
        .flat()
        .join('/');
      return path;
    }
    return outlet.activatedRouteData;
  }

  initRobinChat() {
    if ((<any>window).__robin === undefined) {
      var robin_settings = {
        animateTab: false,
        hide_tab: true,
      };

      const $scriptSettings = document.createElement('script');
      $scriptSettings.id = 'robin_settings';
      $scriptSettings.type = 'text/javascript';
      $scriptSettings.innerHTML = `var robin_settings = ${JSON.stringify(robin_settings)}`;
      this.document.body.append($scriptSettings);

      const $script = document.createElement('script');
      $script.id = 'RobinChat';
      $script.async = true;
      $script.type = 'text/javascript';
      $script.src = 'https://selfservice.robinhq.com/external/robin/9g08q40k.js';

      document.body.append($script);
    }
  }
}
